import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';
import { AllSelection } from 'prosemirror-state';
import { rowTypeAttr, colgroupAttr } from './config/constants';
import { DOMSerializer } from './DOMSerializer';
var blockWrappers = [
    'div', 'ol', 'ul', 'li', 'table', 'tbody', 'thead', 'tfoot', 'caption', 'td', 'th', 'p',
    'tr', 'col', 'colgroup', 'article', 'main', 'nav', 'header', 'footer', 'aside', 'section'
];
var removeRowType = function (table, nodeName) {
    var wrapper = (table.ownerDocument || document).createElement(nodeName);
    Array.from(table.rows).filter(function (r) { return r.getAttribute(rowTypeAttr) === nodeName; }).forEach(function (row) {
        row.removeAttribute(rowTypeAttr);
        wrapper.appendChild(row);
    });
    if (wrapper.children.length) {
        table.appendChild(wrapper);
    }
};
var restoreTables = function (fragment) {
    Array.from(fragment.querySelectorAll('table')).forEach(function (table) {
        removeRowType(table, 'thead');
        removeRowType(table, 'tbody');
        removeRowType(table, 'tfoot');
        var emptyElement = Array.from(table.children).find(function (el) { return el.children.length === 0; });
        if (emptyElement) {
            emptyElement.remove();
        }
        var wrapper = table.parentNode instanceof HTMLDivElement ? table.parentNode : null;
        if (wrapper && wrapper.matches('div[table]')) {
            table.style.marginLeft = wrapper.style.marginLeft;
            table.style.marginRight = wrapper.style.marginRight;
            var captionDiv_1 = Array.from(wrapper.children).find(function (el) { return el.matches('div[caption]'); });
            if (captionDiv_1 && captionDiv_1.innerHTML !== '<img>') {
                var caption_1 = table.createCaption();
                if (captionDiv_1.id) {
                    caption_1.id = captionDiv_1.id;
                }
                if (captionDiv_1.className) {
                    caption_1.className = captionDiv_1.className;
                }
                Array.from(captionDiv_1.style).forEach(function (styleName) {
                    caption_1.style[styleName] = captionDiv_1.style[styleName];
                });
                while (captionDiv_1.firstChild) {
                    caption_1.appendChild(captionDiv_1.firstChild);
                }
            }
            if (wrapper.style.width && !table.style.width) {
                table.style.width = wrapper.style.width;
            }
            wrapper.parentNode.insertBefore(table, wrapper);
            wrapper.parentNode.removeChild(wrapper);
        }
    });
};
var setRowType = function (children, nodeName) {
    var tag = nodeName.toUpperCase();
    children.filter(function (c) { return c.nodeName === tag; }).forEach(function (rowsWrapper) {
        Array.from(rowsWrapper.children).forEach(function (row) {
            row.setAttribute(rowTypeAttr, nodeName);
            if (rowsWrapper.parentNode) {
                rowsWrapper.parentNode.insertBefore(row, rowsWrapper);
            }
        });
        rowsWrapper.remove();
    });
};
var validateTablesToPmSchema = function (fragment) {
    Array.from(fragment.querySelectorAll('table')).forEach(function (table) {
        var children = Array.from(table.children);
        if (children.some(function (e) { return e.nodeName === 'THEAD' || e.nodeName === 'TFOOT'; })) {
            setRowType(children, 'thead');
            setRowType(children, 'tbody');
            setRowType(children, 'tfoot');
        }
        var colgroup = children.find(function (c) { return c.nodeName === 'COLGROUP'; });
        if (colgroup) {
            table.setAttribute(colgroupAttr, colgroup.outerHTML);
            colgroup.remove();
        }
        if (table.caption || table.style.marginLeft || table.style.marginRight) {
            var wrapper = document.createElement('div');
            wrapper.setAttribute('table', '');
            wrapper.style.display = 'table';
            wrapper.style.marginLeft = table.style.marginLeft;
            wrapper.style.marginRight = table.style.marginRight;
            if (table.caption) {
                var captionDiv_2 = document.createElement('div');
                captionDiv_2.setAttribute('caption', '');
                if (table.caption.id) {
                    captionDiv_2.id = table.caption.id;
                }
                if (table.caption.className) {
                    captionDiv_2.className = table.caption.className;
                }
                Array.from(table.caption.style).forEach(function (styleName) {
                    captionDiv_2.style[styleName] = table.caption.style[styleName];
                });
                while (table.caption.firstChild) {
                    captionDiv_2.appendChild(table.caption.firstChild);
                }
                table.removeChild(table.caption);
                wrapper.appendChild(captionDiv_2);
            }
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
            if (/%/.test(table.style.width)) {
                wrapper.style.width = table.style.width;
                table.style.width = '';
            }
        }
    });
};
/**
 * Trims the whitespace around the provided block nodes.
 *
 * @param html - Input HTML content
 * @param trimAroundTags - Block elements to which trimming will be applied.
 * Defaults to block nodes of the current default schema:
 * 'div', 'ol', 'ul', 'li', 'table', 'tbody', 'thead', 'tfoot', 'td', 'th', 'p'
 * and additional table and semantic nodes from the default Angular Editor schema:
 * 'tr', 'col', 'colgroup', 'article', 'main', 'nav', 'header', 'footer', 'aside', 'section'
 *
 * @returns The trimmed HTML content
 */
export var trimWhitespace = function (html, trimAroundTags) {
    if (trimAroundTags === void 0) { trimAroundTags = blockWrappers; }
    var tags = trimAroundTags.join('|');
    return html.replace(new RegExp('\\s*(<(?:' + tags + ')(?:\\s[^>]*?)?>)', 'g'), '$1')
        .replace(new RegExp('(<\\/(?:' + tags + ')(?:\\s[^>]*?)?>)\\s*', 'g'), '$1');
};
var styleAttr = 'data-style';
var styleReplace = ' ' + styleAttr + '=';
var reTag = /<[^>]+>/gm;
var reStyle = /\sstyle=/gm;
var replacer = function (match) {
    return match.replace(reStyle, styleReplace);
};
var replaceStyleAttr = function (html) {
    return html.replace(reTag, replacer);
};
var applyStyle = function (styleString, element) {
    return styleString.split(';').filter(function (s) { return s.trim() !== ''; }).forEach(function (s) {
        var parts = s.split(':');
        element.style[parts[0].trim()] = parts[1].trim();
    });
};
var restoreStyleAttr = function (container) {
    Array.from(container.querySelectorAll('[' + styleAttr + ']')).forEach(function (element) {
        var styleString = element.getAttribute(styleAttr);
        element.removeAttribute(styleAttr);
        applyStyle(styleString, element);
    });
};
/**
 * Creates a DocumentFragment from the given HTML content.
 *
 * @param html
 * @returns DocumentFragment
 */
export var htmlToFragment = function (html) {
    var template = document.createElement('template');
    template.innerHTML = replaceStyleAttr(html);
    restoreStyleAttr(template.content);
    return template.content;
};
/**
 * @hidden
 */
export var fragmentToHtml = function (fragment) {
    return Array.from(fragment.childNodes).reduce(function (acc, cur) { return acc + (cur.outerHTML || cur.textContent || ''); }, '');
};
/**
 * Creates a DocumentFragment from the given ProseMirrorNode.
 *
 * @param doc ProseMirrorNode
 * @returns DocumentFragment
 */
export var pmDocToFragment = function (doc) {
    var fragment = DOMSerializer.fromSchema(doc.type.schema).serializeFragment(doc.content);
    restoreTables(fragment);
    return fragment;
};
/**
 * Creates a ProseMirrorNode from the given DOM element.
 *
 * @param dom
 * @param schema
 * @param parseOptions
 * @returns ProseMirrorNode
 */
export var domToPmDoc = function (dom, schema, parseOptions) {
    return ProseMirrorDOMParser.fromSchema(schema).parse(dom, parseOptions);
};
/**
 * Creates a ProseMirrorNode from the given HTML content.
 *
 * @param content - The new HTML content.
 * @param schema - The document schema.
 * @param parseOptions - ProseMirror parse options recognized by the `parse` and `parseSlice` methods.
 * @returns - New ProseMirrorNode instance.
 */
export var parseContent = function (content, schema, parseOptions) {
    var dom = htmlToFragment(content);
    validateTablesToPmSchema(dom);
    return domToPmDoc(dom, schema, parseOptions);
};
/**
 * A function that serializes the Editor State content as HTML string.
 *
 * @param state - The Editor State
 * @returns - The serialized content
 */
export var getHtml = function (state) {
    var fragment = pmDocToFragment(state.doc);
    return fragmentToHtml(fragment);
};
/**
 * Replaces the content of the editor with a new one.
 *
 * @param content - The new HTML content.
 * @param commandName - The name of the command.
 * @param parseOptions - ProseMirror parse options recognized by the `parse` and `parseSlice` methods.
 * @returns - Command function that takes an editor `state` and `dispatch` function.
 */
export var setHtml = function (content, command, parseOptions) {
    if (command === void 0) { command = 'setHTML'; }
    if (parseOptions === void 0) { parseOptions = { preserveWhitespace: 'full' }; }
    return function (state, dispatch) {
        return dispatch(state.tr
            .setSelection(new AllSelection(state.doc))
            .replaceSelectionWith(parseContent(content, state.schema, parseOptions))
            .setMeta('commandName', command));
    };
};
